<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import V2ReservationTableGroupModal from '@/components/modals/reservations/V2ReservationTableGroupModal.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import V2ReservationTableGroupRow from '@/components/v2/reservations/tables/V2ReservationTableGroupRow.vue';
  import V2ReservationTablesEditor from '@/components/v2/reservations/tables/V2ReservationTablesEditor.vue';
  import V2ReservationsNotice from '../V2ReservationsNotice.vue';
  import { useReservationFloorPlan } from '@/composition/reservations/use-reservation-floor-plan';
  import { useReservationTablesDraft } from '@/composition/reservations/use-reservation-tables-draft';
  import { useModal } from '@/composition/use-modal';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { onlineCoverage } from '@/domain/reservations/reservation-capacity';
  import type { IOnlineCoverageBand } from '@/domain/reservations/reservation-capacity';
  import { tablesWithoutSeatsFromStore } from '@/domain/reservations/reservation-setup';
  import { DEFAULT_TABLE_SEATS } from '@/domain/reservations/reservation-tables-draft';
  import { isOnlineReservationTable } from '@/domain/reservations/reservation-tables';
  import type {
    IReservationSettingsConfiguration,
    IReservationTableGroup,
  } from '@/interfaces/IReservationSettings';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  /**
   * Ajustes › Mesas: the setup guide's plan editor with a 600 ms autosave,
   * the online-coverage strip and the combinations list.
   */
  const AUTOSAVE_MS = 600;
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    /** Guests can book online in the unsaved draft; otherwise coverage is moot. */
    onlineBooking: boolean;
    /** Online party limits from the unsaved draft, so the coverage follows edits. */
    minPartyOnline: number;
    maxPartyOnline: number;
  }>();
  const emit = defineEmits<{
    (event: 'saved'): void;
    /** The owner chose to lower the online maximum to what tables seat. */
    (event: 'lower-max', max: number): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const store = useRestaurantStore();
  const { width } = useStableWindowSize();
  const allSeats = ref(DEFAULT_TABLE_SEATS);
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const scope = () => ({
    restaurantId: props.configuration.settings.restaurantId,
    branchId: props.configuration.settings.branchId,
  });
  const scopeKey = computed(
    () =>
      `${props.configuration.settings.restaurantId}:${props.configuration.settings.branchId}`
  );
  const draft = useReservationTablesDraft(() => props.configuration, {
    autosaveMs: AUTOSAVE_MS,
    onSaved: () => emit('saved'),
    onError: (cause) => ui.serverError(cause, tr('reservations.tables.error')),
  });
  const floorPlan = useReservationFloorPlan(scope, () => draft.tables.value);
  onMounted(() => void floorPlan.load());
  // The composable resets itself on the same change; load after that reset lands.
  watch(scopeKey, () => void nextTick(() => floorPlan.load()));
  defineExpose({
    flush: draft.flush,
    discard: draft.discard,
    hasPending: () => draft.dirty.value || draft.saving.value,
  });
  /** A write failed: refresh the server rows first, then send what is still dirty. */
  async function retry() {
    emit('saved');
    await draft.flush();
  }
  /** Whether the scope the owner acted on is still the one on screen. */
  function sameScope(configuration: IReservationSettingsConfiguration) {
    return (
      alive &&
      props.configuration.settings.restaurantId ===
        configuration.settings.restaurantId &&
      props.configuration.settings.branchId ===
        configuration.settings.branchId &&
      store.restaurant?.id === configuration.settings.restaurantId &&
      store.selectedBranch?.id === configuration.settings.branchId
    );
  }

  const tables = draft.tables;
  const tablesWithoutSeats = computed(() =>
    tablesWithoutSeatsFromStore(tables.value)
  );
  // Who can book online, from the draft (edits count before they save).
  const coverage = computed(() =>
    onlineCoverage(
      tables.value,
      props.configuration.tableGroups,
      props.minPartyOnline,
      props.maxPartyOnline
    )
  );
  const partyGap = computed(
    () =>
      coverage.value.maxParty > 0 &&
      coverage.value.maxParty < props.maxPartyOnline
  );
  const nothingOnline = computed(
    () =>
      tables.value.length > 0 &&
      coverage.value.maxParty === 0 &&
      tablesWithoutSeats.value === 0
  );
  const onlineTablesCount = computed(
    () =>
      tables.value.filter(
        (table) => isOnlineReservationTable(table) && table.capacity > 0
      ).length
  );
  // A combination the owner already made that guests never get, because a
  // member is not online: the fix is one click on that member, so only offer
  // it when the combination would then really seat larger groups.
  const blockedGroup = computed(() => {
    for (const group of props.configuration.tableGroups) {
      const members = group.tableIds.map((id) =>
        tables.value.find((table) => table.id === id)
      );
      if (members.some((table) => !table || !table.enabled)) continue;
      const seats = Math.min(
        group.maxParty,
        members.reduce((total, table) => total + (table?.capacity ?? 0), 0)
      );
      if (seats < group.minParty || seats <= coverage.value.maxParty) continue;
      const offline = members.find(
        (table) => table && !isOnlineReservationTable(table)
      );
      if (offline) return { group, table: offline };
    }
    return null;
  });
  // The biggest table with seats that guests cannot book.
  const offlineTable = computed(
    () =>
      [...tables.value]
        .filter(
          (table) =>
            table.enabled &&
            !isOnlineReservationTable(table) &&
            table.capacity > coverage.value.maxParty
        )
        .sort((a, b) => b.capacity - a.capacity)[0] ?? null
  );
  /** The table the notices offer to put online, if any. */
  const tableToEnable = computed(
    () => blockedGroup.value?.table ?? offlineTable.value
  );
  function offlineTableText(): string {
    return offlineTable.value
      ? tr('reservations.notice.partyGapTable', {
          table: offlineTable.value.name,
          seats: offlineTable.value.capacity,
        })
      : '';
  }
  const gapNoticeText = computed(() => {
    const range = rangeLabel(coverage.value.maxParty + 1, props.maxPartyOnline);
    const reason = blockedGroup.value
      ? tr('reservations.notice.partyGapGroup', {
          group: blockedGroup.value.group.name,
          table: blockedGroup.value.table.name,
        })
      : offlineTableText();
    return [tr('reservations.notice.partyGap', { range }), reason]
      .filter(Boolean)
      .join(' ');
  });
  const noneOnlineNoticeText = computed(() =>
    [tr('reservations.notice.noOnlineTables'), offlineTableText()]
      .filter(Boolean)
      .join(' ')
  );
  function rangeLabel(from: number, to: number): string {
    return from === to
      ? String(from)
      : tr('reservations.tables.coverageRange', { from, to });
  }
  function bandOptions(band: IOnlineCoverageBand): string {
    const tablesText = String(
      proxy.$tc('reservations.tables.coverageTables', band.tables, {
        count: band.tables,
      })
    );
    const groupsText = String(
      proxy.$tc('reservations.tables.coverageGroups', band.groups, {
        count: band.groups,
      })
    );
    if (band.tables && band.groups)
      return tr('reservations.tables.coverageBoth', {
        tables: tablesText,
        groups: groupsText,
      });
    if (band.tables) return tablesText;
    if (band.groups) return groupsText;
    return tr('reservations.tables.coverageNone');
  }
  function enableTable(id: number) {
    draft.setMode([id], 'online');
  }
  const managePath = computed(
    () => `/${store.restaurant?.stringId ?? ''}/tables`
  );
  /** A modal is being opened: the pending write must land first, once. */
  const opening = ref(false);
  /** Opens the combination modal on top of what the draft shows, once it is saved. */
  async function edit(group?: IReservationTableGroup) {
    if (opening.value) return;
    const configuration = props.configuration;
    opening.value = true;
    try {
      // A failed write already raised its toast; the modal would show stale seats.
      if (!(await draft.flush()) || !sameScope(configuration)) return;
      showGroupModal(group);
    } finally {
      if (alive) opening.value = false;
    }
  }
  function showGroupModal(group?: IReservationTableGroup) {
    modals.show(
      V2ReservationTableGroupModal,
      {
        configuration: { ...props.configuration, tables: tables.value },
        group,
      },
      {
        name: 'v2-reservation-table-group',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        // A 16-column plan at 44px cells; phones fall back to 95% of the screen.
        width: 800,
        maxWidth: Math.round(width.value * 0.95),
        clickToClose: false,
      },
      {
        saved: () => {
          if (alive) emit('saved');
        },
      }
    );
  }
</script>

<template>
  <div class="reservation-tables-settings">
    <PosUiSettingsCard
      flush
      :title="$t('reservations.settings.nav.tables')"
      :description="$t('reservations.tables.description')"
    >
      <template #badge>
        <PosUiBadge
          v-if="tables.length && onlineBooking"
          class="coverage-badge"
          :color="
            coverage.maxParty === 0
              ? tablesWithoutSeats
                ? 'warning'
                : 'error'
              : partyGap
              ? 'warning'
              : 'neutral'
          "
        >
          {{
            coverage.maxParty > 0
              ? $t('reservations.tables.maxOnline', { max: coverage.maxParty })
              : $t('reservations.tables.noneOnline')
          }}
        </PosUiBadge>
      </template>
      <template #actions>
        <router-link class="manage-link" :to="managePath">{{
          $t('reservations.tables.manage')
        }}</router-link>
      </template>
      <div
        v-if="onlineBooking && coverage.maxParty > 0"
        class="card-block coverage"
      >
        <h3 class="coverage-title">
          {{ $t('reservations.tables.coverageTitle') }}
        </h3>
        <ul class="coverage-bands">
          <li
            v-for="band in coverage.bands"
            :key="band.from"
            class="coverage-band"
            :class="{ covered: band.tables + band.groups > 0 }"
          >
            <PosUiIcon
              :name="band.tables + band.groups > 0 ? 'check' : 'close'"
              :size="16"
            />
            <span>{{
              $t('reservations.tables.coverageBand', {
                range: rangeLabel(band.from, band.to),
              })
            }}</span>
            <span class="band-options">· {{ bandOptions(band) }}</span>
          </li>
        </ul>
        <!-- Tables without seats come first: fixing them may close the gap. -->
        <V2ReservationsNotice
          v-if="partyGap && !tablesWithoutSeats"
          class="coverage-notice"
          icon="alert-outline"
          :text="gapNoticeText"
        >
          <template #actions>
            <PosUiButton
              v-if="tableToEnable"
              color="neutral"
              variant="outline"
              @click="enableTable(tableToEnable.id)"
              >{{
                $t('reservations.notice.enableOnline', {
                  table: tableToEnable.name,
                })
              }}</PosUiButton
            >
            <PosUiButton
              v-if="onlineTablesCount >= 2"
              color="neutral"
              variant="outline"
              :disabled="opening"
              @click="edit()"
              >{{ $t('reservations.notice.combineTables') }}</PosUiButton
            >
            <PosUiButton
              color="neutral"
              variant="outline"
              @click="emit('lower-max', coverage.maxParty)"
              >{{
                $t('reservations.notice.lowerMax', { max: coverage.maxParty })
              }}</PosUiButton
            >
          </template>
        </V2ReservationsNotice>
      </div>
      <div v-else-if="onlineBooking && nothingOnline" class="card-block">
        <V2ReservationsNotice
          class="coverage-notice"
          tone="danger"
          icon="alert-circle-outline"
          :text="noneOnlineNoticeText"
        >
          <template v-if="tableToEnable" #actions>
            <PosUiButton
              color="neutral"
              variant="outline"
              @click="enableTable(tableToEnable.id)"
              >{{
                $t('reservations.notice.enableOnline', {
                  table: tableToEnable.name,
                })
              }}</PosUiButton
            >
          </template>
        </V2ReservationsNotice>
      </div>
      <div v-if="tablesWithoutSeats > 0" class="card-block">
        <V2ReservationsNotice
          class="seatless-notice"
          :text="
            $tc('reservations.notice.tablesWithoutSeats', tablesWithoutSeats, {
              count: tablesWithoutSeats,
            })
          "
          :link="{ to: managePath, label: $t('reservations.tables.manage') }"
        />
      </div>
      <div v-if="draft.failed.value" class="card-block">
        <V2ReservationsNotice
          class="save-error"
          tone="danger"
          :text="$t('reservations.tables.error')"
          :action-label="$t('reservations.page.retry')"
          @action="retry"
        />
      </div>
      <PosUiEmptyState
        v-if="!tables.length"
        icon="table-chair"
        :title="$t('reservations.tables.emptyTitle')"
        :description="$t('reservations.tables.emptyDescription')"
        ><template #action
          ><router-link class="manage-link" :to="managePath">{{
            $t('reservations.tables.manage')
          }}</router-link></template
        ></PosUiEmptyState
      >
      <div v-else class="card-block editor-block">
        <V2ReservationTablesEditor
          :configuration="configuration"
          :draft="draft"
          :floor-plan="floorPlan"
          mode-control="segmented"
          :all-seats="allSeats"
          @update:allSeats="allSeats = $event"
        />
      </div>
      <template #footer>{{ $t('reservations.tables.modeHint') }}</template>
    </PosUiSettingsCard>

    <PosUiSettingsCard
      flush
      :title="$t('reservations.groups.title')"
      :description="$t('reservations.groups.description')"
    >
      <template #actions>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="plus"
          :disabled="tables.length < 2 || opening"
          @click="edit()"
          >{{ $t('reservations.groups.add') }}</PosUiButton
        >
      </template>
      <PosUiEmptyState
        v-if="!configuration.tableGroups.length"
        icon="table-multiple"
        :title="$t('reservations.groups.emptyTitle')"
        :description="$t('reservations.groups.description')"
      />
      <div v-else class="groups-list">
        <V2ReservationTableGroupRow
          v-for="group in configuration.tableGroups"
          :key="group.id"
          class="group-row"
          :group="group"
          :tables="tables"
          with-menu
          :disabled="opening"
          @saved="emit('saved')"
          @removed="emit('saved')"
          @edit="edit($event)"
        />
      </div>
    </PosUiSettingsCard>
  </div>
</template>

<style scoped lang="scss">
  .reservation-tables-settings {
    @apply tw-flex tw-flex-col tw-gap-6;
    // Same look as a neutral outline PosUiButton, which has no link mode.
    .manage-link {
      @apply tw-inline-flex tw-h-11 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-4 tw-text-sm tw-font-medium tw-text-neutral-700 tw-no-underline hover:tw-bg-neutral-100 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
    }
    .card-block {
      @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-4 md:tw-px-6;
      .reservations-notice {
        @apply tw-mb-0;
      }
      .coverage-bands + .coverage-notice {
        @apply tw-mt-4;
      }
      &:last-child {
        @apply tw-border-b-0;
      }
    }
    .editor-block {
      @apply tw-py-5;
    }
    .coverage-title {
      @apply tw-m-0 tw-mb-2 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .coverage-bands {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0;
    }
    // One pill per group of party sizes: green when a table seats them.
    .coverage-band {
      @apply tw-inline-flex tw-min-h-9 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-px-2.5 tw-text-sm tw-text-amber-800;
      .band-options {
        @apply tw-text-xs tw-text-amber-700;
      }
      &.covered {
        @apply tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-800;
        .band-options {
          @apply tw-text-emerald-700;
        }
      }
    }
    .group-row {
      @apply tw-px-5 tw-py-4 md:tw-px-6;
      & + .group-row {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }
  }
</style>
